import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { LoadingSpinner } from '@/common/LoadingSpinner';
import LureoButton from '@/common/LureoButton';
import { CheckCircleIcon, XCircleIcon } from '@phosphor-icons/react';
import { Card } from '@/components/ui/card';
import { Note } from '@/components/ui/note';
import { TextLink } from '@/components/ui/text-button';
import { PageTitle } from '@/components/ui/text';
import { toast } from 'sonner';
import { useServices } from '@/infrastructure/providers/ServiceProvider';
import { PageFrame } from '@/components/ui/page-frame';

export default function BillingSuccess() {
  const services = useServices();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'verifying' | 'success' | 'error'>('verifying');
  const [message, setMessage] = useState('Verifying your payment...');
  const [canRetry, setCanRetry] = useState(false);

  const handleVerifySession = async (sessionId: string) => {
    try {
      const result = await services.billing.verifySession({ session_id: sessionId });

      if (result.success) {
        setStatus('success');
        setMessage('Plan upgraded successfully!');
        
        toast.success('Payment verified successfully!');

        // Redirect to dashboard after 2.5 seconds
        setTimeout(() => {
          navigate('/dashboard');
        }, 2500);
      } else {
        throw new Error('Payment verification was not successful');
      }

      return result;
    } catch (error) {
      console.error('❌ Error in handleVerifySession:', error);
      setStatus('error');
      setMessage(error instanceof Error ? error.message : 'Failed to verify payment');
      setCanRetry(true);
      // Error toast already shown by billingService
      throw error;
    }
  };

  const verifySessionRef = useRef(handleVerifySession);
  useLayoutEffect(() => {
    verifySessionRef.current = handleVerifySession;
  });

  const handleRetry = () => {
    const sessionId = searchParams.get('session_id') || searchParams.get('sid');
    if (sessionId) {
      setStatus('verifying');
      setMessage('Verifying your payment...');
      setCanRetry(false);
      handleVerifySession(sessionId);
    }
  };

  const [prevSearchParams, setPrevSearchParams] = useState<URLSearchParams | null>(null);
  if (prevSearchParams !== searchParams) {
    setPrevSearchParams(searchParams);
    if (!(searchParams.get('session_id') || searchParams.get('sid'))) {
      setStatus('error');
      setMessage('Missing payment session information. Please try again or contact support.');
      setCanRetry(false);
    }
  }

  useEffect(() => {
    const sessionId = searchParams.get('session_id') || searchParams.get('sid');

    // No id is shown as an error by the render below
    if (!sessionId) return;
    verifySessionRef.current(sessionId);
  }, [searchParams]);

  return (
    <PageFrame layout="centred" surface="own" className="bg-lureo-surface bg-linear-to-br from-lureo-neutral-subtle via-lureo-accent/10 to-lureo-brand/5">
      <Card elevation="high" padding="lg" className="w-full max-w-md rounded-lureo-radius-xl text-center">
        {/* Icon */}
        <div className="mb-6 flex justify-center">
          {status === 'verifying' && (
            <div className="bg-lureo-accent/20 rounded-full p-6">
              <LoadingSpinner size="lg" />
            </div>
          )}
          {status === 'success' && (
            <div className="bg-lureo-success/15 rounded-full p-6">
              <CheckCircleIcon className="w-16 h-16 text-lureo-success-ink" weight="fill" />
            </div>
          )}
          {status === 'error' && (
            <div className="bg-lureo-danger/10 rounded-full p-6">
              <XCircleIcon className="w-16 h-16 text-lureo-danger-ink" weight="fill" />
            </div>
          )}
        </div>

        {/* Title */}
        <PageTitle className="mb-3">
          {status === 'verifying' && 'Processing Payment'}
          {status === 'success' && 'Payment Successful!'}
          {status === 'error' && 'Verification Failed'}
        </PageTitle>

        {/* Message */}
        <p className="text-lureo-ink-subtle mb-8 leading-relaxed">
          {message}
        </p>

        {/* Actions */}
        <div className="space-y-3">
          {status === 'success' && (
            <div className="flex items-center justify-center gap-2 text-lureo-14 text-lureo-ink-subtle leading-5">
              <LoadingSpinner size="sm" />
              <span>Redirecting to dashboard...</span>
            </div>
          )}

          {status === 'error' && (
            <>
              {canRetry && (
                <LureoButton
                  onClick={handleRetry}
                  variant="secondary"
                  className="w-full h-12 rounded-lureo-radius-button-pill px-6"
                >
                  Try Again
                </LureoButton>
              )}
              <LureoButton
                onClick={() => navigate('/dashboard')}
                variant="outline-3"
                className="w-full h-12 rounded-lureo-radius-button-pill px-6"
              >
                Return to Dashboard
              </LureoButton>
              <p className="text-lureo-14 text-lureo-ink-subtle mt-4 leading-5">
                Need help? <TextLink href="mailto:team@lureo.com">Contact support</TextLink>
              </p>
            </>
          )}

          {status === 'verifying' && (
            <LureoButton
              onClick={() => navigate('/dashboard')}
              variant="outline-3"
              className="w-full h-12 rounded-lureo-radius-button-pill px-6"
            >
              Cancel
            </LureoButton>
          )}
        </div>

        {/* Note for long processing times */}
        {status === 'verifying' && (
          <Note tone="warning" className="mt-6 text-left">
            This may take a few moments. Please don't close this window.
          </Note>
        )}
      </Card>
    </PageFrame>
  );
}

